import type { JSONContent } from "@tiptap/core";
import z from "zod";
import type { FileUpload } from "./FileUpload";
import type { MailboxTabular, RfqQuote, Tabular } from "./RFQ";
import Fetcher from "../Fetcher";

export type RFQMailbox = {
    id: number;
    createdAt: string;
    noQuote: boolean;
    vendorRelationshipId: number;
    vendorName: string | null;

    communications: RFQCommunication[];
    draftCommunications: RFQDraftCommunication[];

    // Calculated by the backend
    mailboxTitle: string; // Either the vendor name or the first email address
    contactCount: number; // Number of participants categorized as "CONTACT"
    latestQuoteId: number | null;
    failedToSend: boolean;
    hasNewReply: boolean;
    lastMessageAt: string | null; // Date of most recent communication

    followUpItems: RFQMailboxFollowUpItem[];

    // These only matter during RFQ configure AKA who is being sent the RFQ for a vendorRelationship
    mailboxContacts: RFQMailboxContact[];

    // Tables/Quotes for the awards section
    tables: MailboxTabular[];
};

export type RFQMailboxFollowUpItem = {
    id: number;
    content: string;
    response: string;
    createdAt: string;
    mailboxId: number;
};

export type RFQCommunication = {
    id: number;
    message: string; // Raw or HTML content of the email sent, or communication to be shown.
    direction: RFQCommunicationDirection;
    channel: RFQCommunicationChannel;
    createdAt: string; // ISO date string
    updatedAt: string; // ISO date string
    quoteExtractionStartedAt: string | null; // ISO date string, set when quote extraction begins
    isRead: boolean;
    broadcast: boolean;
    noQuote: boolean | null;
    mailboxId: number;
    email?: RFQEmail | null;
    emailBounces: RFQEmailBounce[];
    participants: RFQParticipant[];
    rfqQuotes: RfqQuote[];

    // initiatedByUserId?: number | null; // Who sent/received this communication [Platform User].
    // initiatedByUser?: {
    //     id: number;
    //     first_name: string;
    //     last_name: string;
    //     email: string;
    // } | null;
};

export type RFQEmailBounce = {
    id: number;
    bounceId: string;
    bounceType: string;
    emailAddress: string;
    reason: string;
    createdAt: string;
    updatedAt: string;
    emailId: number;
    teamEmailAddressId: number;
};

export type RFQEmail = {
    id: number;
    method: string;
    fromEmail: string;
    toEmail: string[];
    ccEmail: string[];
    bccEmail: string[];
    subject: string;
    textBody: string;
    htmlBody: string;
    createdAt: Date;
    attachments: Attachment[];
};

export type RFQParticipant = {
    displayName: string; // what to display in the UI - calculated contact name or email address
    entityType: RFQParticipantEntityType;
    role: RFQParticipantRole;

    entityId: number | null; // respective to entityType
    senderUserId: number | null; // provided when entityType = "USER_SENDER_EMAIL", denotes which team member the email belongs to

    mailboxId: number;
    communicationId: number;
    emailParticipantId: number;
    teamEmailAddressId: number;
    emailAddress: string;
    contactVendorRelationshipId: number;
};

export type RFQDraftCommunication = {
    id: number;
    message: string;
    messageJson: JSONContent;
    direction: RFQCommunicationDirection;
    channel: RFQCommunicationChannel;
    createdAt: string;
    updatedAt: string;
    initiatedByUserId: number;
    senderId: number | null;
    mailboxId: number;
    attachments: Attachment[];
};

export type UpdateRFQCommunicationParams = { id: number; content?: string; contentJson?: JSONContent; senderId?: number | null };

export type RFQUncategorizedCommunication = {
    id: number;
    message: string;
    direction: RFQCommunicationDirection;
    channel: RFQCommunicationChannel;
    createdAt: string;
    updatedAt: string;
    vendorRelationshipId: number | null;
    email: RFQEmail;
};

type Attachment = {
    id: number; // id of the entity attachment (email_attachment, draft_attachment, etc)
    file: FileUpload;
};

export type RFQCommunicationDirection = "INCOMING" | "OUTGOING";
export type RFQCommunicationChannel = "EMAIL" | "PLATFORM";

const ParticipantRoleSchema = z.enum(["FROM", "TO", "CC", "BCC"]);
export type RFQParticipantRole = z.infer<typeof ParticipantRoleSchema>;

// Keep this in sync with the backend
// Type not defined currently - comes from the query in /v2/rfqs/:rfqUniquePublicId
const ParticipantEntitySchema = z.enum([
    // A user of the platform on this team
    "USER",

    // Platform-user specified colleague, according to team_colleague table
    "TEAM_COLLEAGUE",

    // User on this team has email address connected.
    "USER_EMAIL_SENDER",

    // Active contact on this vendor relationship/mailbox.
    "CONTACT",

    /**
     * Emails that are not classified in any of the above and are one of two things:
        a. Is an internal purchaser email domain used for email ("*.purchaser.dev" or "purchaserquote.com")
        b. Was manually removed by a user from a potential contact. Exists in the potential_vendor_relationship_email_address with deletedAt NOT NULL.
     */
    "IGNORED",

    // Email address is none of the above and/or exists in potential_vendor_relationship_email_address with deletedAt IS NULL
    "UNKNOWN",
]);

export type RFQParticipantEntityType = z.infer<typeof ParticipantEntitySchema>;

export type RFQMailboxContact = {
    email: string;
    emailAddressId: number;
    vendorRelationshipId: number;
    contactId: number;
};

// Matches purchaser-api
const KnownContactSchema = z.object({
    vendorRelationshipId: z.coerce.number().positive(),
    teamEmailAddressId: z.coerce.number().positive(),
});
export type KnownContact = z.infer<typeof KnownContactSchema>;

// Matches purchaser-api
const AddMailboxPayloadSchema = z.discriminatedUnion("type", [
    KnownContactSchema.extend({
        type: z.literal("relationship"),
    }),
    z.object({
        type: z.literal("email"),
        email: z.string().trim().toLowerCase().email(),
    }),
    z.object({
        type: z.literal("list"),
        contactListId: z.coerce.number().positive(),
    }),
]);
export type AddMailboxPayload = z.infer<typeof AddMailboxPayloadSchema>;
export type AddMailboxParams = { rfqUniquePublicId: string; data?: AddMailboxPayload; batch?: AddMailboxPayload[] };

type AddMailboxOrContactsEdits = {
    mailboxId: number;
    teamEmailAddressIds: number[];
}[];

const f = Fetcher.default();

export default {
    getMailboxes: async (rfqUniquePublicId: string) => {
        return await f.get<RFQMailbox[]>(`/v2/mailboxes?rfqUniquePublicId=${rfqUniquePublicId}`);
    },
    getMailbox: async (mailboxId: number) => {
        return await f.get<RFQMailbox>(`/v2/mailboxes/${mailboxId}`);
    },
    addMailbox: async (params: AddMailboxParams) => {
        return await f.post<AddMailboxOrContactsEdits>("/v2/mailboxes", { body: params });
    },
    addMailboxContact: async (params: { mailboxId: number; contact: KnownContact }) => {
        return await f.post(`/v2/mailboxes/${params.mailboxId}/contacts`, { body: params.contact });
    },
    deleteMailboxContact: async (params: { mailboxId: number; contact: KnownContact }) => {
        return await f.delete(`/v2/mailboxes/${params.mailboxId}/contacts`, { body: params.contact });
    },
    deleteMailboxFollowUps: async (params: { mailboxId: number; followUpIds: number[] }) => {
        if (params.followUpIds.length === 0) return;
        try {
            return await f.delete(`/v2/mailboxes/${params.mailboxId}/follow-ups`, { body: params.followUpIds });
        } catch (e) {
            // Don't throw if follow up deletion failed
            console.error(`Failed to delete followUpIds ${params.followUpIds} for mailboxId ${params.mailboxId}`);
        }
    },
};
